if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();
          this.updateProductInfo(html);
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          // this.updateGallerySlider(html);
          
          this.updateWishlistVariant(variant)
          this.updateMobileCartBtn(html);
          
          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          this.productForm?.toggleSubmitButton(
            html.getElementById(`mob-add-to-cart`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }
      // custom product info on the pdp
       updateProductInfo(html) {
        console.log('updateProductInfo');
        // Run jdgmCacheServer.reloadAll() only if available
        if (typeof jdgmCacheServer.reloadAll !== "undefined") {
          jdgmCacheServer.reloadAll();
          console.log('review reload all run')
        }
        try {
            // Check if slick is initialized before trying to unslick
            const mainSlider = jQuery('.product-details-slider');
            const thumbSlider = jQuery('.product-item-slider');
            const cloudSlider = jQuery('.cloud-slider');
            const similarSlider = jQuery('.similar-slider');
            const pdpFeaturesSlider = jQuery('.pdp-features-slider');
            const shopCollectionSlider = jQuery('.shop-collection-slider');

            if (mainSlider.hasClass('slick-initialized')) {
              mainSlider.slick('unslick');
            }
            
            if (thumbSlider.hasClass('slick-initialized')) {
              thumbSlider.slick('unslick');
            }

            if (cloudSlider.hasClass('slick-initialized')) {
              cloudSlider.slick('unslick');
            }
            if (cloudSlider.hasClass('slick-initialized') && cloudSlider ) {
              cloudSlider.slick('unslick');
            }
            if (pdpFeaturesSlider.hasClass('slick-initialized')) {
              pdpFeaturesSlider.slick('unslick');
            }
            if (shopCollectionSlider.hasClass('slick-initialized')) {
              shopCollectionSlider.slick('unslick');
            }
            
            setTimeout(() => {
              if (typeof initFeaturedTabs === 'function') {
                initFeaturedTabs();
              };

              // Reinitialize slick slider
              jQuery('.shop-collection-slider').slick({
                slidesToShow: 3.5,
                slidesToScroll: 1,
                arrows: false,
                infinite: false,
                  responsive: [
                  {
                    breakpoint: 767,
                    settings: {
                      arrows: false,
                      dots: false,
                      infinite: false,
                      slidesToShow: 2.3,
                      slidesToScroll: 1
                    }
                  }
                ]
              });
              jQuery('.pdp-features-slider').slick({
                slidesToShow: 3.5,
                slidesToScroll: 1,
                arrows: false,
                infinite: false,
                  responsive: [
                  {
                    breakpoint: 1199,
                    settings: {
                      arrows: false,
                      dots: false,
                      infinite: false,
                      slidesToShow: 2.5,
                      slidesToScroll: 1
                    }
                  },
                  {
                    breakpoint: 767,
                    settings: {
                      arrows: false,
                      dots: false,
                      infinite: false,
                      slidesToShow: 1.5,
                      slidesToScroll: 1
                    }
                  }
                ]
              });
              if(window.matchMedia("(max-width: 767px)").matches){
              jQuery('.product-details-slider').slick({
                slidesToShow: 1,
                slidesToScroll: 1, 
                arrows: false,
                dots: true,
                infinite: false,
                asNavFor: '.product-item-slider'
              });
              jQuery('.product-item-slider').slick({
                slidesToShow: 3,
                slidesToScroll: 1,
                swipeToSlide: false,
                asNavFor: '.product-details-slider',
                dots: false,
                arrows: false,
                infinite: false,
                vertical: false,
                focusOnSelect: true
              });
              
              jQuery('.cloud-slider').slick({
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: false,
                infinite: false,
        		  });
                // reinitialize mobile thumb image
                const variantImages = document.querySelectorAll('.product-variant-image');
                const enableLoader = document.querySelector('.product-details-left .slider-rl');

                variantImages.forEach(function(imageEl) {
                  imageEl.addEventListener('click', function() {
                    const optionId = imageEl.getAttribute('data-option-id');
            
                    // Click the corresponding radio input
                    const targetInput = document.querySelector(`input[data-option-value-id="${optionId}"]`);
                    if (targetInput) {
                      targetInput.click();
                    }
            
                    // Remove 'variant-active' from all images
                    document.querySelectorAll('.product-variant-image.active').forEach(function(activeEl) {
                      activeEl.classList.remove('active');
                    });
            
                    // Add 'variant-active' to the clicked image
                    imageEl.classList.add('active');
                    enableLoader.classList.add('loading');
                  });
                });
              };
              var zoomButtons = document.querySelectorAll('.js-photoswipe__zoom');
              zoomButtons.forEach(function(zoomButton) {
                  zoomButton.addEventListener('click', function() {
                      var pswpElement = document.querySelector('.pswp');
                      var galleryItems = document.querySelectorAll('.pdp-gallery');
          
                      var items = Array.from(galleryItems).map(function(item) {
                          var img = item.querySelector('img');
                          return {
                              src: img.src,
                              w: parseInt(img.getAttribute('width'), 10),
                              h: parseInt(img.getAttribute('height'), 10),
                              title: img.alt
                          };
                      });
          
                      var options = {
                          index: Array.from(zoomButtons).indexOf(this),
                          bgOpacity: 0.7,
                          showHideOpacity: true,
                          history: false  // Disable history to prevent URL change
                      };
          
                      var gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
                      gallery.init();
          
                      gallery.listen('close', function() {
                          document.body.style.overflow = 'auto'; // Reset body overflow
                          document.querySelector('.pswp__bg').style.display = 'none'; // Ensure the background is hidden
                      });
                  });
              });
            }, 100);

            
            
          } catch (error) {
            console.error('Error updating product info:', error);
          }
        // }
      }
      
      // custom updateGallerySlider on the pdp
      // updateGallerySlider(html) {
      //   const sourceGallery = html.querySelector('.slider-rl');
      //   const destinationGallery = this.querySelector('.slider-rl');
      //   // console.log('updateGallerySlider');
      //   if (sourceGallery && destinationGallery) {
      //     try {
      //       // Check if slick is initialized before trying to unslick
      //       const mainSlider = jQuery('.product-details-slider');
      //       const thumbSlider = jQuery('.product-item-slider');
      
      //       if (mainSlider.hasClass('slick-initialized')) {
      //         mainSlider.slick('unslick');
      //       }
            
      //       if (thumbSlider.hasClass('slick-initialized')) {
      //         thumbSlider.slick('unslick');
      //       }
            
      //       // Update gallery HTML
      //       destinationGallery.innerHTML = sourceGallery.innerHTML;
            
      //       // Wait for DOM update before reinitializing sliders
      //       setTimeout(() => {
      //         // Reinitialize slick slider
      //         if(window.matchMedia("(max-width: 767px)").matches){
      //         jQuery('.product-details-slider').slick({
      //           slidesToShow: 1,
      //           slidesToScroll: 1, 
      //           arrows: false,
      //           dots: true,
      //           infinite: false,
      //           asNavFor: '.product-item-slider'
      //         });
      
      //         jQuery('.product-item-slider').slick({
      //           slidesToShow: 3,
      //           slidesToScroll: 1,
      //           swipeToSlide: false,
      //           asNavFor: '.product-details-slider',
      //           dots: false,
      //           arrows: false,
      //           infinite: false,
      //           vertical: false,
      //           focusOnSelect: true
      //         });
      //         };
      //         var zoomButtons = document.querySelectorAll('.js-photoswipe__zoom');
      //         zoomButtons.forEach(function(zoomButton) {
      //             zoomButton.addEventListener('click', function() {
      //                 var pswpElement = document.querySelector('.pswp');
      //                 var galleryItems = document.querySelectorAll('.variant-gallery');
          
      //                 var items = Array.from(galleryItems).map(function(item) {
      //                     var img = item.querySelector('img');
      //                     return {
      //                         src: img.src,
      //                         w: parseInt(img.getAttribute('width'), 10),
      //                         h: parseInt(img.getAttribute('height'), 10),
      //                         title: img.alt
      //                     };
      //                 });
          
      //                 var options = {
      //                     index: Array.from(zoomButtons).indexOf(this),
      //                     bgOpacity: 0.7,
      //                     showHideOpacity: true,
      //                     history: false  // Disable history to prevent URL change
      //                 };
          
      //                 var gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
      //                 gallery.init();
          
      //                 gallery.listen('close', function() {
      //                     document.body.style.overflow = 'auto'; // Reset body overflow
      //                     document.querySelector('.pswp__bg').style.display = 'none'; // Ensure the background is hidden
      //                 });
      //             });
      //         });
      //       }, 100);
            
      //     } catch (error) {
      //       console.error('Error updating gallery slider:', error);
      //     }
      //   }
      // }

      updateMobileCartBtn(html) {
        const newMobileATC = html.querySelector('#mob-add-to-cart');
        const mobileATC = this.querySelector('#mob-add-to-cart');
        const desktopCart = this.querySelector(`.addToCartDesktop_live`);
        const newDesktopCart = html.querySelector(`.addToCartDesktop_live`);
        const buyNow = this.querySelectorAll('.gokwik-buy-now');
        const variant = this.getSelectedVariant(html);

        let isDisabled = desktopCart.getAttribute('disabled');
        console.log('newMobileATC',newMobileATC)
        console.log('isDisabled',isDisabled)
        if (newDesktopCart) {
          const newInStockValue = newDesktopCart.getAttribute('data-instock');
          desktopCart.setAttribute('data-instock', newInStockValue);
          buyNow.forEach((btn) => {
            btn.setAttribute('data-instock', newInStockValue);
            btn.setAttribute('data-variant-id', variant?.id);
          });
        }
        if(newMobileATC.hasAttribute('disabled')){
          mobileATC.setAttribute('disabled',1);
          // document.querySelectorAll('.gokwik-buy-now').forEach(function(b){
          //     b.setAttribute('data-instock','false');
          // })
        }else{
          mobileATC.removeAttribute('disabled');
          // document.querySelectorAll('.gokwik-buy-now').forEach(function(b){
          //     b.setAttribute('data-instock','true');
          // })
        }
        // Update gallery HTML
        if (newMobileATC && mobileATC) {
            mobileATC.innerHTML = newMobileATC.innerHTML;
        }
        
      }
      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      updateWishlistVariant(variant){
        
        document.querySelector('product-info .wishlist__button').setAttribute('data-vid',variant.id)
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
        // location.reload(true);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
